// The Card (Basis Theory) add-account flow, ported from platform-app's TBT flow.
// Card data is collected in Basis Theory hosted iframe fields and tokenized client-side
// (the raw PAN never touches this app or our API). The resulting vault token + cardholder
// and billing details are submitted to the v2 API using the scoped session token.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  BasisTheoryProvider,
  CardExpirationDateElement,
  CardNumberElement,
  CardVerificationCodeElement,
  useBasisTheory,
  type BasisTheoryElements,
  type ICardExpirationDateElement,
  type ICardNumberElement,
  type ICardVerificationCodeElement,
} from '@basis-theory/react-elements';
import { FormField, TextField } from '@melio/penny';

import { ApiError } from '@/api/client';
import type { AddressParts, AddressSuggestion } from '@/api/lookup';
import { lookupAddressesWithSession, submitCardFundingSourceWithSession } from '@/api/payouts';
import { tbtConfig, type EnvironmentId } from '@/config/environments';
import type { FundingSourceLinkClaims } from '@/lib/sessionToken';
import { notifyParent } from '@/lib/embedMessages';

import { Spinner } from '@/components/code';
import { Banner, Button, Field, Icon } from '@/components/ds';

type Props = { env: EnvironmentId; token: string; claims: FundingSourceLinkClaims };

// Result of bt.tokenize with our BIN+last4 reveal mask applied to `data.number`.
type TokenizedCard = {
  id: string;
  data: { number: string; expiration_month: string | number; expiration_year: string | number };
};

const NUMBER_MASK = '{{ data.number | slice: 0, 6 }}XXXXXX{{ data.number | last4 }}';

// Basis Theory fields render in iframes that can't read the page's CSS vars or font, so we load
// Poppins into the iframe and use literal values to match the ds Input fields (color #18191b = --text-primary).
const elementStyle = {
  fonts: ['https://fonts.googleapis.com/css2?family=Poppins:wght@400;500&display=swap'],
  base: {
    fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
    fontSize: '15px',
    color: '#18191b',
    lineHeight: '24px',
  },
};

export function AddCardAccount({ env, token, claims }: Props) {
  const { apiKey, environment } = tbtConfig(env);
  const { bt, error } = useBasisTheory(apiKey, { environment });

  if (error) {
    return <Banner kind="critical">Couldn’t initialize secure card fields. Please try again later.</Banner>;
  }

  return (
    <BasisTheoryProvider bt={bt}>
      <CardForm bt={bt} env={env} token={token} entityId={claims.entityId} publishableKey={claims.partnerName} />
    </BasisTheoryProvider>
  );
}

function CardForm({
  bt,
  env,
  token,
  entityId,
  publishableKey,
}: {
  bt: BasisTheoryElements | undefined;
  env: EnvironmentId;
  token: string;
  entityId: string;
  publishableKey: string;
}) {
  const cardNumberRef = useRef<ICardNumberElement>(null);
  const expRef = useRef<ICardExpirationDateElement>(null);
  const cvcRef = useRef<ICardVerificationCodeElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  const detailsFilled = firstName && lastName && line1 && city && state && postalCode;

  const submit = async () => {
    const numberEl = cardNumberRef.current;
    const expEl = expRef.current;
    const cvcEl = cvcRef.current;
    if (!bt || !numberEl || !expEl || !cvcEl) {
      setStatus('error');
      setError('Secure card fields aren’t ready yet. Please wait a moment and try again.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const result = (await bt.tokenize({
        type: 'card',
        data: {
          number: numberEl,
          expiration_month: expEl.month(),
          expiration_year: expEl.year(),
          cvc: cvcEl,
        },
        mask: {
          number: NUMBER_MASK,
          expiration_month: '{{ data.expiration_month }}',
          expiration_year: '{{ data.expiration_year }}',
        },
      })) as unknown as TokenizedCard;

      const number = String(result.data.number);
      const fundingSource = await submitCardFundingSourceWithSession(env, token, entityId, publishableKey, {
        token: result.id,
        cardBin: number.slice(0, 6),
        lastFourDigits: number.slice(-4),
        expirationMonth: String(result.data.expiration_month).padStart(2, '0'),
        expirationYear: String(result.data.expiration_year),
        cardOwner: { firstName, lastName },
        address: { line1, city, state, postalCode },
      });
      setStatus('done');
      notifyParent({ flow: 'add', kind: 'card', status: 'success', accountId: fundingSource.id });
    } catch (ex) {
      const message = ex instanceof ApiError ? ex.message : 'We couldn’t save your card. Check the details and try again.';
      setStatus('error');
      setError(message);
      notifyParent({ flow: 'add', kind: 'card', status: 'error', message });
    }
  };

  if (status === 'done') {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={successIconWrap}>
          <Icon name="checked-circle-fill" size={28} color="var(--success, #1b9b5e)" />
        </div>
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Card added</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>You’re all set — you can close this window.</div>
      </div>
    );
  }

  const busy = status === 'submitting';

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 6 }}>Add your card</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Your card details are entered securely and never touch our servers.
        </div>
      </div>

      {error && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SecureField label="Card number">
          {(h) => <CardNumberElement id="card-number" ref={cardNumberRef} placeholder="1234 1234 1234 1234" style={elementStyle} {...h} />}
        </SecureField>
        <div style={{ display: 'flex', gap: 12 }}>
          <SecureField label="Expiration" style={{ flex: 1, minWidth: 0 }}>
            {(h) => <CardExpirationDateElement id="card-expiration" ref={expRef} placeholder="MM/YY" style={elementStyle} {...h} />}
          </SecureField>
          <SecureField label="CVC" style={{ flex: 1, minWidth: 0 }}>
            {(h) => <CardVerificationCodeElement id="card-cvc" ref={cvcRef} placeholder="CVC" style={elementStyle} {...h} />}
          </SecureField>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FormField
              labelProps={{ label: 'First name' }}
              render={(props) => (
                <TextField {...props} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" />
              )}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FormField
              labelProps={{ label: 'Last name' }}
              render={(props) => (
                <TextField {...props} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" />
              )}
            />
          </div>
        </div>

        <AddressAutocomplete
          env={env}
          token={token}
          entityId={entityId}
          label="Billing address"
          value={line1}
          onChange={setLine1}
          onPick={(p) => {
            setLine1(p.line1 ?? '');
            setCity(p.city ?? '');
            setState(p.state ?? '');
            setPostalCode(p.postalCode ?? '');
          }}
        />
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2, minWidth: 0 }}>
            <FormField
              labelProps={{ label: 'City' }}
              render={(props) => <TextField {...props} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Hollywood" />}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FormField
              labelProps={{ label: 'State' }}
              render={(props) => <TextField {...props} value={state} onChange={(e) => setState(e.target.value)} placeholder="CA" />}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FormField
              labelProps={{ label: 'ZIP' }}
              render={(props) => (
                <TextField {...props} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="90001" />
              )}
            />
          </div>
        </div>
      </div>

      <Button onClick={submit} disabled={!bt || busy || !detailsFilled} fullWidth size="large" style={{ marginTop: 20 }}>
        {busy ? <Spinner size={16} color="#fff" /> : 'Add card'}
      </Button>
    </div>
  );
}

/**
 * Domestic address autocomplete backed by `GET /v2/lookup/addresses`. Picking a suggestion
 * fills the editable line1/city/state/postalCode fields below it (same model as the
 * onboarding `AddressFields`), so the user can still adjust them by hand.
 */
function AddressAutocomplete({
  env,
  token,
  entityId,
  label,
  value,
  onChange,
  onPick,
}: {
  env: EnvironmentId;
  token: string;
  entityId: string;
  label: string;
  /** The billing street line — this field IS the address line, not a separate search box. */
  value: string;
  onChange: (value: string) => void;
  onPick: (parts: AddressParts) => void;
}) {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Picking a suggestion updates `value`, which would otherwise re-trigger the search; skip it once.
  const skipNextSearch = useRef(false);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    if (value.trim().length < 3) {
      setOptions([]);
      return undefined;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const handle = setTimeout(() => {
      lookupAddressesWithSession(env, token, entityId, value, ctrl.signal)
        .then(setOptions)
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      clearTimeout(handle);
      ctrl.abort();
    };
  }, [value, env, token, entityId]);

  const pick = (option: AddressSuggestion) => {
    skipNextSearch.current = true;
    if (option.addressParts) onPick(option.addressParts);
    else onChange(option.formattedText);
    setOpen(false);
    setOptions([]);
  };

  const showDropdown = open && value.trim().length >= 3;

  return (
    <FormField
      labelProps={{ label }}
      render={(props) => (
        <div style={{ position: 'relative' }}>
          <TextField
            {...props}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setOpen(true);
            }}
            placeholder="Start typing your address"
          />
          {showDropdown && (
            <div style={dropdownWrap}>
              {loading && options.length === 0 && <div style={dropdownEmpty}>Searching…</div>}
              {!loading && options.length === 0 && (
                <div style={dropdownEmpty}>No matches yet — keep typing, or just enter your address.</div>
              )}
              {options.map((o) => (
                <button key={o.id} type="button" style={dropdownOption} onClick={() => pick(o)}>
                  {o.formattedText}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    />
  );
}

// Matches the penny TextField box (large = 48px tall, 1px interactive border, 8px radius) so the
// Basis Theory iframe fields line up with the penny name/address fields above and below.
const btFieldWrap = {
  display: 'flex',
  alignItems: 'center',
  height: 48,
  border: '1px solid var(--border-interactive)',
  borderRadius: 'var(--radius-input)',
  background: '#fff',
  padding: '0 14px',
  overflow: 'hidden',
  transition: 'border-color .1s ease-in-out, outline-color .1s ease-in-out',
} as const;

// penny's `focusStyles` for inputs: brand-700 border + a solid 2px `--focus-primary` outline,
// offset 2px (semantic.border.brand + semantic.focus.primary). Mirrors @melio/penny TextField.
const btFieldWrapFocus: CSSProperties = {
  borderColor: 'var(--brand-700)',
  outline: '2px solid var(--focus-primary)',
  outlineOffset: '2px',
};

const btInner = { flex: 1, minWidth: 0 } as const;

/**
 * Wraps a Basis Theory iframe element so it carries the same border + focus treatment as penny's
 * TextField. The element's content lives in an iframe (styled via `elementStyle`) but it emits
 * focus/blur to us, so we toggle penny's focus styling on the surrounding box. `children` is a
 * render fn that receives the `onFocus`/`onBlur` handlers to spread onto the BT element.
 */
function SecureField({
  label,
  style,
  children,
}: {
  label: string;
  style?: CSSProperties;
  children: (handlers: { onFocus: () => void; onBlur: () => void }) => ReactNode;
}) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <Field label={label} style={style}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...btFieldWrap,
          ...(hovered && !focused ? { borderColor: 'var(--border-interactive-hover)' } : {}),
          ...(focused ? btFieldWrapFocus : {}),
        }}
      >
        <div style={btInner}>{children({ onFocus: () => setFocused(true), onBlur: () => setFocused(false) })}</div>
      </div>
    </Field>
  );
}

const dropdownWrap = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 20,
  background: '#fff',
  border: '1px solid var(--border-static, #d6d6d6)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,.12)',
  maxHeight: 240,
  overflowY: 'auto',
} as const;

const dropdownOption = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  color: 'var(--text-primary)',
} as const;

const dropdownEmpty = {
  padding: '10px 12px',
  fontSize: 13,
  color: 'var(--text-secondary)',
} as const;

const successIconWrap = {
  width: 52,
  height: 52,
  borderRadius: '50%',
  background: 'var(--success-soft, #e7f6ee)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
} as const;
